/**
 * useAggregatedWalletData Hook
 * React Query hook for fetching wallet data with multi-provider fallback
 *
 * Uses:
 * - DeBank (primary) - Best DeFi/multi-chain coverage
 * - Moralis (fallback) - Good token/NFT data
 * - Alchemy (last resort) - Basic Ethereum token balances
 */

import { useQuery } from '@tanstack/react-query';
import { walletDataAggregator, getProviderStatus } from '../../services/walletDataAggregator';
import type { Chain, AggregatedWalletData, UnifiedToken, DeFiPosition, NFTItem } from '../../types/wallet.types';

// Re-export types for convenience
export type { AggregatedWalletData, UnifiedToken, DeFiPosition, NFTItem };

/**
 * Hook to get complete aggregated wallet data
 * Automatically fetches from best available provider with fallback
 */
export function useAggregatedWalletData(
  address: string | null,
  options: {
    enabled?: boolean;
    includeNFTs?: boolean;
    includeDeFi?: boolean;
    refetchInterval?: number;
  } = {}
) {
  const {
    enabled = true,
    includeNFTs = true,
    includeDeFi = true,
    refetchInterval = 60000
  } = options;

  const providerStatus = getProviderStatus();
  const hasProvider = providerStatus.debank.available || providerStatus.moralis.available || providerStatus.alchemy.available;

  return useQuery({
    queryKey: ['aggregated-wallet-data', address, includeNFTs, includeDeFi],
    queryFn: async (): Promise<AggregatedWalletData> => {
      if (!address) {
        throw new Error('No address provided');
      }

      return walletDataAggregator.getAggregatedWalletData(address, {
        includeNFTs,
        includeDeFi
      });
    },
    enabled: !!address && enabled && hasProvider,
    staleTime: 30000, // 30 seconds
    gcTime: 300000, // 5 minutes
    refetchInterval,
    retry: 2,
    retryDelay: 1000
  });
}

/**
 * Hook to get token balances only (faster)
 */
export function useWalletTokens(
  address: string | null,
  options: {
    enabled?: boolean;
    chain?: Chain;
    refetchInterval?: number;
  } = {}
) {
  const { enabled = true, chain, refetchInterval = 60000 } = options;

  const providerStatus = getProviderStatus();
  const hasProvider = providerStatus.debank.available || providerStatus.moralis.available;

  return useQuery({
    queryKey: ['wallet-tokens', address, chain],
    queryFn: async (): Promise<UnifiedToken[]> => {
      if (!address) {
        throw new Error('No address provided');
      }

      return walletDataAggregator.getTokenBalances(address, chain);
    },
    enabled: !!address && enabled && hasProvider,
    staleTime: 30000,
    gcTime: 300000,
    refetchInterval,
    retry: 2,
    retryDelay: 1000
  });
}

/**
 * Hook to get DeFi positions
 */
export function useWalletDeFi(
  address: string | null,
  options: {
    enabled?: boolean;
    refetchInterval?: number;
  } = {}
) {
  const { enabled = true, refetchInterval = 120000 } = options;

  const providerStatus = getProviderStatus();
  const hasProvider = providerStatus.debank.available || providerStatus.moralis.available;

  return useQuery({
    queryKey: ['wallet-defi', address],
    queryFn: async (): Promise<DeFiPosition[]> => {
      if (!address) {
        throw new Error('No address provided');
      }

      return walletDataAggregator.getDeFiPositions(address);
    },
    enabled: !!address && enabled && hasProvider,
    staleTime: 60000, // DeFi positions change less frequently
    gcTime: 300000,
    refetchInterval,
    retry: 2,
    retryDelay: 1000
  });
}

/**
 * Hook to get NFTs
 */
export function useWalletNFTs(
  address: string | null,
  options: {
    enabled?: boolean;
    chain?: Chain;
    refetchInterval?: number;
  } = {}
) {
  const { enabled = true, chain, refetchInterval = 300000 } = options;

  const providerStatus = getProviderStatus();
  const hasProvider = providerStatus.debank.available || providerStatus.moralis.available;

  return useQuery({
    queryKey: ['wallet-nfts', address, chain],
    queryFn: async (): Promise<NFTItem[]> => {
      if (!address) {
        throw new Error('No address provided');
      }

      return walletDataAggregator.getNFTs(address, chain);
    },
    enabled: !!address && enabled && hasProvider,
    staleTime: 120000, // NFTs change even less frequently
    gcTime: 600000, // 10 minutes cache
    refetchInterval,
    retry: 2,
    retryDelay: 1000
  });
}

/**
 * Hook to get total portfolio value
 */
export function useWalletTotalValue(
  address: string | null,
  options: {
    enabled?: boolean;
    refetchInterval?: number;
  } = {}
) {
  const { enabled = true, refetchInterval = 30000 } = options;

  const providerStatus = getProviderStatus();
  const hasProvider = providerStatus.debank.available || providerStatus.moralis.available;

  return useQuery({
    queryKey: ['wallet-total-value', address],
    queryFn: async (): Promise<number> => {
      if (!address) {
        return 0;
      }

      return walletDataAggregator.getTotalValue(address);
    },
    enabled: !!address && enabled && hasProvider,
    staleTime: 15000, // More frequent updates for value
    gcTime: 60000,
    refetchInterval,
    retry: 2,
    retryDelay: 1000
  });
}

/**
 * Hook to check provider availability
 */
export function useProviderStatus() {
  return useQuery({
    queryKey: ['provider-status'],
    queryFn: () => getProviderStatus(),
    staleTime: 300000, // 5 minutes
    gcTime: 600000
  });
}

/**
 * Helper to format USD value
 */
export function formatUSD(value: number): string {
  if (value === 0) return '$0.00';
  if (value < 0.01) return `$${value.toFixed(6)}`;
  if (value < 1) return `$${value.toFixed(4)}`;
  if (value < 1000) return `$${value.toFixed(2)}`;
  if (value < 1000000) return `$${(value / 1000).toFixed(2)}K`;
  if (value < 1000000000) return `$${(value / 1000000).toFixed(2)}M`;
  return `$${(value / 1000000000).toFixed(2)}B`;
}

/**
 * Helper to format token balance
 */
export function formatTokenBalance(balance: number, decimals: number = 4): string {
  if (balance === 0) return '0';
  if (balance < 0.0001) return '< 0.0001';
  if (balance < 1) return balance.toFixed(decimals);
  if (balance < 1000) return balance.toFixed(2);
  if (balance < 1000000) return `${(balance / 1000).toFixed(2)}K`;
  return `${(balance / 1000000).toFixed(2)}M`;
}

/**
 * Helper to format percentage change
 */
export function formatChange(change: number | undefined): string {
  if (change === undefined || change === null) return '-';
  const sign = change >= 0 ? '+' : '';
  return `${sign}${change.toFixed(2)}%`;
}
